import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'component-library';
import { useTranslation } from 'react-i18next';

import { PlotReportFactElement, PlotReportFactLabelNameEnum, R1FactType } from '@/api/rest/resources/types/fact';
import { reverseProtectedAreas } from '@/api/rest/resources/types/protectedAreas';
import { UnitEnum } from '@/api/rest/resources/types/units';
import { Area } from '@/utils/area';
import { isNullish } from '@/utils/booleans';

const UNITS = {
  distance: UnitEnum.km,
  area: UnitEnum['km^2'],
} as const;

export const ProtectedAreasTable = ({ data }: { data: PlotReportFactElement[] }) => {
  const { t } = useTranslation();

  const areaInBufferData = data.flatMap((item) => {
    if (item.name !== R1FactType.protected_areas_area) {
      return [];
    }

    const protectionCategory = item.labels?.find(
      (label) => label.name === PlotReportFactLabelNameEnum.protection_category,
    )?.value as keyof typeof reverseProtectedAreas | undefined;

    if (!protectionCategory) {
      return [];
    }

    const category = reverseProtectedAreas[`${protectionCategory}`];

    if (!category || typeof item.value !== 'number') {
      return [];
    }

    return {
      category,
      area_in_buffer: parseFloat(
        new Area({
          value: item.value,
          dimension: 'squareMeters',
        })
          .toSquareKilometers()
          .value.toFixed(2),
      ),
    };
  });

  const nearestDistanceData = data.flatMap((item) => {
    if (item.name !== R1FactType.protected_areas_min_distance) {
      return [];
    }

    const protectionCategory = item.labels?.find(
      (label) => label.name === PlotReportFactLabelNameEnum.protection_category,
    )?.value as keyof typeof reverseProtectedAreas | undefined;

    if (!protectionCategory) {
      return [];
    }

    const category = reverseProtectedAreas[`${protectionCategory}`];

    if (!category || typeof item.value !== 'number') {
      return [];
    }

    const nearestDistance = item.unit === UnitEnum.m ? item.value / 1000 : item.value;

    return {
      category,
      nearest_distance: parseFloat(nearestDistance.toFixed(2)),
    };
  });

  const formattedData = Object.values(reverseProtectedAreas).flatMap((category) => {
    const areaInBuffer = areaInBufferData.find((item) => item.category === category)?.area_in_buffer;
    const nearestDistance = nearestDistanceData.find((item) => item.category === category)?.nearest_distance;

    if (isNullish(areaInBuffer) && isNullish(nearestDistance)) {
      return [];
    }

    const areaCategory = t(`global.protectedAreas.categories.${category}`);
    const areaCategoryDescription = t(`global.protectedAreas.description.${category}`);

    return {
      category_label: `${areaCategory} - ${areaCategoryDescription}`,
      area_in_buffer: areaInBuffer ?? '--',
      nearest_distance: nearestDistance ?? '--',
    };
  });

  const columns = [
    {
      accessorKey: 'category_label',
      header: t('shared.ncaDetail.details.protectedAreas.stats.header.table.category'),
    },
    {
      accessorKey: 'nearest_distance',
      header: t('shared.ncaDetail.details.protectedAreas.stats.header.table.nearestDistance', {
        unit: UNITS.distance,
      }),
    },
    {
      accessorKey: 'area_in_buffer',
      header: t('shared.ncaDetail.details.protectedAreas.stats.header.table.areaInBuffer', {
        unit: UNITS.area,
      }),
    },
  ];

  return (
    <div className='max-h-[400px] overflow-y-auto w-full'>
      <Table data-testid='iucn-category-table'>
        <TableHeader>
          <TableRow>
            {columns.map((column) => (
              <TableHead key={column.accessorKey} className='text-center'>
                {column.header}
              </TableHead>
            ))}
            <th></th>
          </TableRow>
        </TableHeader>
        <TableBody>
          {formattedData.map((row, index) => (
            <TableRow key={index}>
              <Cell value={row.category_label} />
              <Cell value={row.nearest_distance} />
              <Cell value={row.area_in_buffer} />
            </TableRow>
          ))}
          {formattedData.length === 0 && (
            <TableRow>
              <td colSpan={columns.length} className='text-center typography-body2 text-text-secondary py-4'>
                {t('shared.ncaDetail.details.protectedAreas.stats.header.table.noData')}
              </td>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
};

const Cell = ({ value }: { value: string | number }) => {
  return <TableCell className='text-center'>{value}</TableCell>;
};
